import React, { useMemo } from 'react';
import { Paper, Stack, Typography, Skeleton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartsAxis,
  ChartsGrid,
  ChartsTooltip,
  ChartsReferenceLine,
  LinePlot,
  ChartDataProvider,
  ChartsSurface,
} from '@mui/x-charts';
import ChartToggleControls from '../common/ChartToggleControls';
import { NEEDisplayMode } from '../../types/enums';
import { GroupComparisonGroupResult } from '../../types/schema';

interface GroupComparisonChartProps {
  title: string;
  granularity: 'annual' | 'monthly';
  groups: GroupComparisonGroupResult[];
  displayMode: NEEDisplayMode;
  onDisplayModeChange: (mode: NEEDisplayMode) => void;
  loading?: boolean;
}

const getXAxisData = (granularity: 'annual' | 'monthly', groups: GroupComparisonGroupResult[]): Array<number | string> => {
  const labels = new Set<number | string>();

  groups.forEach((group) => {
    if (granularity === 'annual') {
      group.annualData.forEach((item) => labels.add(item.year));
    } else {
      group.monthlyData.forEach((item) => labels.add(item.monthYear));
    }
  });

  const arrayLabels = Array.from(labels);

  if (granularity === 'annual') {
    return (arrayLabels as number[]).sort((a, b) => a - b);
  }

  return (arrayLabels as string[]).sort();
};

const getValueForMode = (
  displayMode: NEEDisplayMode,
  total: number | null | undefined,
  intensity: number | null | undefined,
  scale: number = 1,
): number | null => {
  if (displayMode === NEEDisplayMode.TOTAL) {
    return total !== null && total !== undefined ? total / scale : null;
  }
  return intensity ?? null;
};

export const GroupComparisonChart: React.FC<GroupComparisonChartProps> = ({
  title,
  granularity,
  groups,
  displayMode,
  onDisplayModeChange,
  loading = false,
}) => {
  const theme = useTheme();

  const xAxisData = useMemo(() => getXAxisData(granularity, groups), [granularity, groups]);

  // Determine scale based on data values
  const scale = useMemo(() => {
    if (displayMode !== NEEDisplayMode.TOTAL) {
      return 1;
    }
    // Check if any value has absolute value >= 1000
    const hasLargeValues = groups.some((group) => {
      const sourceData = granularity === 'annual' ? group.annualData : group.monthlyData;
      return sourceData.some((item) => Math.abs(item.neeTotal) >= 1000);
    });
    return hasLargeValues ? 1000 : 1;
  }, [groups, displayMode, granularity]);

  const series = useMemo(() => {
    return groups.map((group, index) => {
      const sourceData = granularity === 'annual' ? group.annualData : group.monthlyData;
      const lookup = new Map<number | string, { total: number | null | undefined; intensity: number | null | undefined }>();

      sourceData.forEach((item) => {
        if (granularity === 'annual') {
          lookup.set(item.year, { total: item.neeTotal, intensity: item.neeIntensity });
        } else {
          lookup.set(item.monthYear, { total: item.neeTotal, intensity: item.neeIntensity });
        }
      });

      const data = xAxisData.map((label) => {
        const values = lookup.get(label);
        if (!values) {
          return null;
        }
        return getValueForMode(displayMode, values.total, values.intensity, scale);
      });

      const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.warning.main];

      return {
        id: group.label || `Group ${index + 1}`,
        label: group.label || `Group ${index + 1}`,
        data,
        type: 'line' as const,
        curve: 'linear' as const,
        color: colors[index % colors.length],
      };
    });
  }, [groups, xAxisData, displayMode, granularity, scale, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.warning.main]);

  const hasData = series.some((serie) => serie.data.some((value) => value !== null));

  const yAxisLabel = displayMode === NEEDisplayMode.TOTAL
    ? (scale === 1000 ? 'NEE Total (ktCO2)' : 'NEE Total (tCO2)')
    : 'NEE Intensity (tCO2/ha)';
  const xAxisLabel = granularity === 'annual' ? 'Year' : 'Month';

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <ChartToggleControls displayMode={displayMode} onDisplayModeChange={onDisplayModeChange} />
      </Stack>

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={320} animation="wave" />
          <Skeleton variant="text" width="60%" animation="wave" />
        </Stack>
      ) : hasData ? (
        <>
          <ChartDataProvider
            xAxis={[
              {
                scaleType: 'band',
                data: xAxisData,
                label: xAxisLabel,
                valueFormatter: (value) => String(value),
              },
            ]}
            yAxis={[
              {
                label: yAxisLabel,
                valueFormatter: (value) => value.toFixed(1),
              },
            ]}
            series={series}
            height={340}
            margin={{ left: 20, right:20, top: 20, bottom: 20 }}
          >
            <ChartsSurface>
              <ChartsGrid />
              <LinePlot />
              <ChartsReferenceLine
                y={0}
                lineStyle={{
                  stroke: theme.palette.grey[400],
                  strokeWidth: 1,
                }}
              />
              <ChartsAxis />
              <ChartsTooltip />
            </ChartsSurface>
          </ChartDataProvider>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 3 }}>
            {series.map((serie) => (
              <Stack
                key={serie.id}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 16,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: serie.color,
                  }}
                />
                <Typography variant="body2">{serie.label}</Typography>
              </Stack>
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No comparison data available.</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default GroupComparisonChart;
